import React, { useState } from 'react';
import { Box, Typography, Button, CircularProgress, Collapse, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useLegalAuth as useAuth } from '../hooks/useLegalAuth';
import { getConsentHistory } from '../components/services/consentService';
import DeleteAccountDialog from '../components/DeleteAccountDialog';

const ProfileLegal = () => {
  const navigate = useNavigate();
  const { profileDocId, isSuperAdmin } = useAuth();
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);

  const loadHistory = async () => {
    if (!profileDocId) return; // SuperAdmin has no profile doc — nothing to load
    setShowHistory(true);
    if (!history) {
      const h = await getConsentHistory(profileDocId);
      const sorted = [...h].sort((a, b) => {
        const aTime = a.acceptedAt?.toDate ? a.acceptedAt.toDate().getTime() : 0;
        const bTime = b.acceptedAt?.toDate ? b.acceptedAt.toDate().getTime() : 0;
        return bTime - aTime;
      });
      setHistory(sorted);
    }
  };

  const latestRecord = history && history.length > 0 ? history[0] : null;

  return (
    <Box sx={{ maxWidth: 940, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, md: 4 } }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 3,
        borderBottom: '1px solid #E9E7F2',
        minHeight: 86,
        width: '100%',
      }}>
        <Button
          variant="text"
          onClick={() => navigate('/audits')}
          sx={{
            minWidth: 0,
            width: 32,
            height: 32,
            borderRadius: '50%',
            color: '#4F46E5',
            background: 'transparent',
            p: 0,
            ml: -1.5,
            boxShadow: 'none',
            '&:hover': { background: '#F4F1FF', color: '#382EC7' },
            '&:active': { background: '#EEEAFE' },
          }}
          aria-label="Back to reports"
        >
          <ArrowBackIcon sx={{ fontSize: 20 }} />
        </Button>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: '#1A0F2E',
            letterSpacing: '-0.04em',
            whiteSpace: 'nowrap',
            fontSize: { xs: '1.8rem', sm: '2.1rem' },
            lineHeight: 1.2,
          }}
        >
          Legal & Account
        </Typography>
      </Box>

                <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        {isSuperAdmin ? (
          <Box sx={{ p: 3, borderRadius: 3, border: '1px solid #E7ECF3', background: '#F9FBFF', mb: 3 }}>
            <Typography sx={{ fontSize: 15, color: '#4B4660', lineHeight: 1.7 }}>
              Terms &amp; Conditions consent tracking does not apply to Super Admin accounts.
              Super Admin access is managed separately and is not subject to per-user T&amp;C acceptance.
            </Typography>
          </Box>
        ) : (
        <>
        <Button
          variant="outlined"
          onClick={loadHistory}
          sx={{
            mb: 3,
            textTransform: 'none',
            fontWeight: 700,
            color: '#0F8BEA',
            borderColor: '#9FD9FF',
            background: '#F4FBFF',
            borderRadius: 2,
            px: 2.5,
            py: 1,
            minWidth: 180,
            display: 'block',
            marginLeft: 0,
            ml: 0,
            '&:hover': { background: '#EAF8FF', borderColor: '#8AD0FF' },
          }}
        >
          {showHistory ? 'Refresh' : 'View'} consent history
        </Button>

        {showHistory && !history && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, mb: 3 }}>
            <CircularProgress size={20} sx={{ color: '#7C3AED' }} />
            <Typography sx={{ color: '#4B4660', fontSize: 14 }}>Loading latest consent record...</Typography>
          </Box>
        )}

        {showHistory && history && (
          <Box sx={{ mb: 4 }}>
            {history.length === 0 && (
              <Box sx={{ p: 3, borderRadius: 3, border: '1px solid #E7ECF3', background: '#F9FBFF' }}>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1A0F2E', mb: 1 }}>
                  No consent record found
                </Typography>
                <Typography sx={{ color: '#4B4660', lineHeight: 1.7 }}>
                  No consent record has been captured yet. Complete the final Terms & Conditions review to record your legal acceptance.
                </Typography>
              </Box>
            )}

            {history.length > 0 && history.map((record, index) => {
              const formatDate = (ts) => {
                if (!ts?.toDate) return 'pending...';
                const d = ts.toDate();
                const day = String(d.getDate()).padStart(2, '0');
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const year = d.getFullYear();
                const hours = String(d.getHours()).padStart(2, '0');
                const minutes = String(d.getMinutes()).padStart(2, '0');
                return `${day}/${month}/${year} ${hours}:${minutes}`;
              };

              return (
                <Box
                  key={index}
                  sx={{
                    p: 3,
                    mb: 2,
                    borderRadius: 3,
                    border: '1px solid #E4E0F9',
                    background: 'linear-gradient(180deg, #F9F8FF 0%, #F4F7FF 100%)',
                    boxShadow: '0 8px 24px rgba(79, 70, 229, 0.08)',
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2.5,
                    pb: 1.5,
                    borderBottom: '1px solid rgba(79, 70, 229, 0.12)',
                  }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#1A0F2E' }}>
                      {index === 0 ? 'Latest T&C acceptance' : `Previous acceptance`}
                    </Typography>
                    {index === 0 && (
                      <Box sx={{
                        px: 1.25, py: 0.5, borderRadius: 99, background: '#EDE9FE', color: '#4F46E5',
                        fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase',
                      }}>
                        Active
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ display: 'grid', gap: 1.3 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1A0F2E' }}>
                      <Box component="span" sx={{ color: '#5B4BDB', mr: 0.5 }}>T&C version:</Box>
                      {record.version}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#4B4660' }}>
                      <Box component="span" sx={{ fontWeight: 700, color: '#1A0F2E', mr: 0.5 }}>Accepted on:</Box>
                      {formatDate(record.acceptedAt)}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#4B4660' }}>
                      <Box component="span" sx={{ fontWeight: 700, color: '#1A0F2E', mr: 0.5 }}>IP address:</Box>
                      {record.ipAddress || 'unknown'}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#4B4660' }}>
                      <Box component="span" sx={{ fontWeight: 700, color: '#1A0F2E', mr: 0.5 }}>City:</Box>
                      {record.city || 'unknown'}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#4B4660' }}>
                      <Box component="span" sx={{ fontWeight: 700, color: '#1A0F2E', mr: 0.5 }}>State:</Box>
                      {record.state || 'unknown'}
                    </Typography>
                  </Box>

                  <Button
                    size="small"
                    onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                    sx={{ textTransform: 'none', fontWeight: 700, color: '#4F46E5', mt: 2, p: 0 }}
                  >
                    {expandedIndex === index ? 'Hide the exact text I agreed to ▲' : 'View the exact text I agreed to ▼'}
                  </Button>

                  <Collapse in={expandedIndex === index}>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{
                      maxHeight: 300, overflowY: 'auto', p: 2, borderRadius: 2,
                      background: '#FFFFFF', border: '1px solid #ECE9F4',
                    }}>
                      <Typography sx={{ fontSize: 13, color: '#2B233D', whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                        {record.content || 'Full text not available for this record.'}
                      </Typography>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        )}
        </>
        )}

        <Box sx={{ borderTop: '1px solid #E9E7F2', pt: 3 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 20, color: '#1A0F2E', mb: 1 }}>Delete account</Typography>
          <Typography sx={{ fontSize: 14, color: '#6B6480', mb: 2.5 }}>
            Not available for Enterprise plans — contact your tenant administrator instead.
          </Typography>
          <Button
            color="error"
            variant="outlined"
            onClick={() => setDeleteOpen(true)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 2.5,
              py: 1,
              borderColor: '#F0B3B3',
              color: '#D93B49',
              background: '#FFF7F7',
              '&:hover': { background: '#FFF0F0', borderColor: '#E88D8D' },
            }}
          >
            Delete My Account
          </Button>
        </Box>
      </Box>

      <DeleteAccountDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} />
    </Box>
  );
};

export default ProfileLegal;